import tw from "twin.macro"
import { StackedImageModuleProps } from "./StackedImageModule.d"
import ImageBlockFullWidth from "./ImageBlockFullWidth"
import CopyBlock from "./CopyBlock"
import ImageBlock2Left1Right from "./ImageBlock2Left1Right"
import ImageBlock1Left2Right from "./ImageBlock1Left2Right"
import { imageWithAlt } from "../../../../global"
import { useEffect, useState } from "react"
import ImageModal from "./ImageModal"
import { Image } from "../../../atoms/Image"
import Icon from "../../../atoms/Icon"

const StackedImageModule: React.FC<StackedImageModuleProps> = ({
  backgroundColor,
  contentBlocks,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [expandedImage, setExpandedImage] = useState<imageWithAlt | null>(null)
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
    null,
  )
  const [clickedImage, setClickedImage] = useState<string | null>(null)

  // Compile all images from contentBlocks to use in Modal Gallery and Mobile View
  const allImages = (
    contentBlocks: StackedImageModuleProps["contentBlocks"],
  ): imageWithAlt[] => {
    const images: imageWithAlt[] = []

    contentBlocks?.forEach(block => {
      switch (block._type) {
        case "imageBlock1Left2Right":
          if (block.imageLeft) images.push(block.imageLeft)
          if (block.imageTopRight) images.push(block.imageTopRight)
          if (block.imageBottomRight) images.push(block.imageBottomRight)
          break
        case "imageBlock2Left1Right":
          if (block.imageTopLeft) images.push(block.imageTopLeft)
          if (block.imageBottomLeft) images.push(block.imageBottomLeft)
          if (block.imageRight) images.push(block.imageRight)
          break
        case "imageBlockFullWidth":
          if (block.image) images.push(block.image)
          break
      }
    })

    return images
  }
  useEffect(() => {
    if (clickedImage) {
      const index = allImages(contentBlocks).findIndex(
        image => image.image.asset._id == clickedImage,
      )

      setExpandedImageIndex(index)
      setExpandedImage(allImages(contentBlocks)[index])
      setModalOpen(true)
    }
  }, [clickedImage])

  function handleClose() {
    setModalOpen(!modalOpen)
    setExpandedImageIndex(null)
    setExpandedImage(null)
    setClickedImage(null)
    document.body.style.overflow = "auto"
  }

  return (
    <section
      css={[
        tw`py-20`,
        backgroundColor && `background: ${`${backgroundColor?.hex}`};`,
      ]}
    >
      {/* DESKTOP VIEW */}
      <div
        css={[tw`hidden lg:(max-w-[1440px] mx-auto px-10 flex flex-col gap-5)`]}
      >
        {contentBlocks &&
          contentBlocks.map((block, index) => {
            const firstElement = index == 0
            const lastElement = index == contentBlocks.length - 1
            return (
              <div key={block._key}>
                {block._type == "copyBlock" && (
                  <CopyBlock
                    {...block}
                    firstElement={firstElement}
                    lastElement={lastElement}
                  />
                )}
                {block._type == "imageBlockFullWidth" && (
                  <ImageBlockFullWidth
                    {...block}
                    setClickedImage={setClickedImage}
                  />
                )}
                {block._type == "imageBlock2Left1Right" && (
                  <ImageBlock2Left1Right
                    {...block}
                    setClickedImage={setClickedImage}
                  />
                )}
                {block._type == "imageBlock1Left2Right" && (
                  <ImageBlock1Left2Right
                    {...block}
                    setClickedImage={setClickedImage}
                  />
                )}
              </div>
            )
          })}
      </div>
      {/* MOBILE VIEW */}

      <div css={[tw`flex flex-col gap-5 lg:hidden`]}>
        {contentBlocks &&
          contentBlocks.map((block, index) => {
            const firstElement = index == 0
            const lastElement = index == contentBlocks.length - 1
            return (
              <div css={[tw`px-10 `]} key={block._key}>
                {block._type == "copyBlock" && (
                  <CopyBlock
                    {...block}
                    firstElement={firstElement}
                    lastElement={lastElement}
                  />
                )}
              </div>
            )
          })}
        <div css={[tw`w-full`]}>
          {allImages && (
            <div
              css={tw`px-10 overflow-x-auto whitespace-nowrap scrollbar-hide`}
            >
              {allImages(contentBlocks).map((image, index) => (
                <div key={index} css={tw`relative inline-block mr-5`}>
                  <Image
                    imageData={image?.image}
                    css={[tw`w-[380px] h-[555px] object-cover rounded-2xl`]}
                  />
                  <button
                    onClick={() => {
                      setClickedImage(image?.image?.asset?._id)
                    }}
                    css={[
                      tw`absolute z-[49] top-4 right-4 bg-gray-100 rounded-full shadow-2 p-2 px-2! opacity-100 hover:opacity-80`,
                    ]}
                  >
                    <Icon.Expand color={"black"} css={[tw`h-6 w-6`]} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* MODAL GALLERY */}
      {modalOpen && (
        <ImageModal
          expandedImage={expandedImage}
          expandedImageIndex={expandedImageIndex}
          setExpandedImage={setExpandedImage}
          setExpandedImageIndex={setExpandedImageIndex}
          slides={allImages(contentBlocks)}
          open={modalOpen}
          onClose={handleClose}
        />
      )}
    </section>
  )
}
export default StackedImageModule
